import { LayerSpecification } from 'maplibre-gl';
import { GDFPalletCode } from '../../gdf/type';
import { WeatherSettings } from '../../settings/settings';
import { isFront, isRaimProduct, isSigWx, isTracksProduct } from '../../utils/utils';
import { airmetsLayer } from './styles/airmets';
import { diversionLayers, faaDelayLayers, rvrLayers, tafLayers } from './styles/airports';
import { raimLayers, tfrLayers, tracksLayers } from './styles/airsapce';
import { severeWeatherProbLayers, tcfLayers } from './styles/convective';
import { customLayers } from './styles/custom';
import { dynamicSuaLayers } from './styles/dsua';
import { fcaLayers } from './styles/fca';
import { frontLayers } from './styles/fronts';
import { lightningJumpGridLayers, lightningProbabilityLayers } from './styles/lightning';
import { metarLayers } from './styles/metar';
import {
    centerWeatherAdvisory,
    freezingLevelsLayers,
    tropicalCyclones,
    tropopauseHeightsLayers,
    tropopauseLowsLayers,
    volcanicAsh,
    volcanicAsh2,
    watchesWarningLayers,
    weatherDepictionLayers
} from './styles/misc';
import { ozoneLayers } from './styles/ozone';
import { pirepAirepLyers, pirepLayers } from './styles/pirep';
import { radarSummaryLayers } from './styles/radsum';
import { jetStreamLayers, sigWxLayers } from './styles/sig-wx';
import { convectiveSigmetsLayer, fpgLayer, sigmetsLayer } from './styles/sigmets';
import { tapsLayers, turbulenceAdvisories } from './styles/turbulence';

export type WeatherLayerSpecification = LayerSpecification & { pickable?: boolean; themed?: true };

// TODO: instead of using settings like this in layer factory, use maplibre global state to toggle things that way. Needs a bug to be resolved in maplibre
export type StyleFactory = (baseId: string, settings: WeatherSettings, productId: number, isLight: boolean) => WeatherLayerSpecification[];

const generic: StyleFactory = (baseId) => {
    return [
        {
            type: 'fill',
            id: baseId + '-fill',
            filter: ['==', ['geometry-type'], 'Polygon'],
            source: baseId,
            paint: {
                'fill-color': 'rgba(0,0,255,0.33)'
            }
        },
        {
            type: 'line',
            id: baseId + '-line',
            source: baseId,
            paint: {
                'line-color': 'rgba(255,0,0,1)',
                'line-width': 2
            }
        },
        {
            type: 'circle',
            id: baseId + 'circle',
            source: baseId,
            paint: {
                'circle-radius': 3,
                'circle-color': 'rgba(0,255,0,0.33)'
            }
        }
    ];
};

export function getStylingFactory(productId: number, palette: GDFPalletCode): StyleFactory {
    // This uses a mix of product id and palette id because some products have no palette id, some have incorrect palette id and some are drawn differently despite having the same palette id.
    // FIXME: Consolidate all of this on the backend. Fix wrong palette codes. Assign palette codes for those products that don't have one, create new palettes if necessary.

    if (isSigWx(productId)) {
        return sigWxLayers;
    }

    if (isFront(productId)) {
        return frontLayers;
    }

    // Metar and taf use the same palette code
    if (productId === 0x61) {
        return metarLayers;
    }

    if (productId === 0x62) {
        return tafLayers;
    }

    if (palette === GDFPalletCode.ccfp) {
        return tcfLayers;
    }

    if (productId === 0x62) {
        return tafLayers;
    }

    if (palette === GDFPalletCode.delay) {
        return faaDelayLayers;
    }

    if (productId === 0x63) {
        return rvrLayers;
    }

    if (productId === 0x64) {
        return diversionLayers;
    }

    // Tracks have palette NONE
    if (isTracksProduct(productId)) {
        return tracksLayers;
    }

    if (palette === GDFPalletCode.tfr) {
        return tfrLayers;
    }

    // RAIM products have precipitation palette
    if (isRaimProduct(productId)) {
        return raimLayers;
    }

    // Pireps have palette NONE
    if (productId === 0x600c || productId === 0x71) {
        return pirepLayers;
    }

    // Pireps/Aireps have palette NONE
    if (productId === 0x6086) {
        return pirepAirepLyers;
    }

    if (palette === GDFPalletCode.fca) {
        return fcaLayers;
    }

    // AFP palette has FCA records
    if (palette === GDFPalletCode.afp) {
        return fcaLayers;
    }

    // This has sigmet palette code but is hard coded to use legacy sigmet colors in fe-client
    if (productId === 0x6008) {
        return fpgLayer;
    }

    if (palette === GDFPalletCode.airmets) {
        return airmetsLayer;
    }

    if (palette === GDFPalletCode.sigmets) {
        return sigmetsLayer;
    }

    if (palette === GDFPalletCode.csigmets) {
        return convectiveSigmetsLayer;
    }

    if (palette === GDFPalletCode.ozone) {
        return ozoneLayers;
    }

    if (palette == GDFPalletCode.dsua) {
        return dynamicSuaLayers;
    }

    if (palette === GDFPalletCode.radsum) {
        return radarSummaryLayers;
    }

    // Delta plots
    if (productId === 0x83) {
        return customLayers;
    }

    if (productId === 0x6040 || productId === 0x6087 || productId === 0x608a || productId === 0x85c0) {
        return volcanicAsh;
    }

    if (productId >= 0x604c && productId <= 0x604f) {
        return volcanicAsh2;
    }

    if (productId === 0x6048 || productId === 0x6041 || productId === 0x85b0 || productId === 0x6088 || productId === 0x6089) {
        return tropicalCyclones;
    }

    if (productId === 0x600d) {
        return turbulenceAdvisories;
    }

    if (productId === 0x85a0) {
        return tropopauseLowsLayers;
    }

    if (productId === 0x8590) {
        return tropopauseHeightsLayers;
    }

    if (productId === 0x000f || productId === 0x0010 || productId === 0x0001) {
        return weatherDepictionLayers;
    }

    if (productId === 0x000e) {
        return freezingLevelsLayers;
    }

    if (palette === GDFPalletCode.lightning_prb) {
        return lightningProbabilityLayers;
    }

    if (palette === GDFPalletCode.lightning_jumpgrid) {
        return lightningJumpGridLayers;
    }

    if (productId === 0x601e) {
        return tapsLayers;
    }

    if (productId === 0x8580) {
        return jetStreamLayers;
    }

    if (palette === GDFPalletCode.watchwarn) {
        return watchesWarningLayers;
    }

    if (productId === 0x0082) {
        return severeWeatherProbLayers;
    }

    if (palette === GDFPalletCode.centerWeatherAdvisory) {
        return centerWeatherAdvisory;
    }

    return generic;
}
